import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  ISettingsRepository,
  CompanySettings,
  UpdateCompanySettingsInput,
  ModuleSetting,
  UserModuleOverride,
  TicketPriceType,
  TicketElementKey,
  SidebarBrandDisplay,
} from '../../../interfaces/src';

const DEFAULT_TICKET_ORDER: TicketElementKey[] = ['companyName', 'name', 'reference', 'price', 'barcode'];
const VALID_KEYS: ReadonlySet<TicketElementKey> = new Set(DEFAULT_TICKET_ORDER);
const SIDEBAR_BRAND_DISPLAY_VALUES: ReadonlySet<SidebarBrandDisplay> = new Set(['logo_only', 'logo_with_name']);

function parseSidebarBrandDisplay(raw: unknown): SidebarBrandDisplay {
  return SIDEBAR_BRAND_DISPLAY_VALUES.has(raw as SidebarBrandDisplay)
    ? (raw as SidebarBrandDisplay)
    : 'logo_with_name';
}

function parseTicketOrder(raw: unknown): TicketElementKey[] {
  if (typeof raw !== 'string' || raw.trim() === '') return [...DEFAULT_TICKET_ORDER];
  const parts = raw.split(',').map((s) => s.trim()).filter((s) => VALID_KEYS.has(s as TicketElementKey)) as TicketElementKey[];
  const seen = new Set<TicketElementKey>();
  const ordered: TicketElementKey[] = [];
  parts.forEach((k) => { if (!seen.has(k)) { ordered.push(k); seen.add(k); } });
  DEFAULT_TICKET_ORDER.forEach((k) => { if (!seen.has(k)) ordered.push(k); });
  return ordered;
}

export class SupabaseSettingsRepository implements ISettingsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getCompanySettings(): Promise<CompanySettings> {
    // Filter by current_user_tenant_id() explicitly so platform admins
    // (whose RLS lets them see every tenant's company_settings) read
    // the SAME tenant that BEFORE-INSERT triggers will write to.
    // Otherwise admin would see "tenant A's settings" while creating
    // articles under "tenant B".
    type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: string | null; error: { message: string } | null }> };
    const { data: tenantId } = await (this.supabase as unknown as SbAny).rpc(
      'current_user_tenant_id',
      {},
    );
    if (!tenantId) throw new Error('not_in_tenant');
    const { data, error } = await this.supabase
      .from('company_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('company_settings_not_found');
    return mapCompanySettings(data);
  }

  async updateCompanySettings(input: UpdateCompanySettingsInput): Promise<CompanySettings> {
    const current = await this.getCompanySettings();
    const mapped: TablesUpdate<'company_settings'> = {};
    if (input.companyName !== undefined) mapped['company_name'] = input.companyName;
    if (input.softwareName !== undefined) mapped['software_name'] = input.softwareName;
    if (input.taxId !== undefined) mapped['tax_id'] = input.taxId;
    if (input.address !== undefined) mapped['address'] = input.address;
    if (input.phone !== undefined) mapped['phone'] = input.phone;
    if (input.email !== undefined) mapped['email'] = input.email;
    if (input.logoUrl !== undefined) mapped['logo_url'] = input.logoUrl;
    if (input.logoDarkUrl !== undefined) mapped['logo_dark_url'] = input.logoDarkUrl;
    if (input.faviconUrl !== undefined) mapped['favicon_url'] = input.faviconUrl;
    if (input.logoSmallUrl !== undefined) mapped['logo_small_url'] = input.logoSmallUrl;
    if (input.sidebarBrandDisplay !== undefined) mapped['sidebar_brand_display'] = input.sidebarBrandDisplay;
    if (input.primaryColor !== undefined) mapped['primary_color'] = input.primaryColor;
    if (input.borderRadius !== undefined) mapped['border_radius'] = input.borderRadius;
    if (input.themeMode !== undefined) mapped['theme_mode'] = input.themeMode;
    if (input.themePreset !== undefined) mapped['theme_preset'] = input.themePreset;
    if (input.defaultVatRate !== undefined) mapped['default_vat_rate'] = input.defaultVatRate;
    if (input.withholdingThreshold !== undefined) mapped['withholding_threshold'] = input.withholdingThreshold;
    if (input.withholdingRate !== undefined) mapped['withholding_rate'] = input.withholdingRate;
    if (input.fiscalStampAmount !== undefined) (mapped as Record<string, unknown>)['fiscal_stamp_amount'] = input.fiscalStampAmount;
    if (input.reuseDeletedNumbers !== undefined) mapped['reuse_deleted_numbers'] = input.reuseDeletedNumbers;
    if (input.showClientCarNumber !== undefined) (mapped as Record<string, unknown>)['show_client_car_number'] = input.showClientCarNumber;
    if (input.tagline !== undefined) (mapped as Record<string, unknown>)['tagline'] = input.tagline;
    if (input.subtitle !== undefined) (mapped as Record<string, unknown>)['subtitle'] = input.subtitle;
    if (input.website !== undefined) (mapped as Record<string, unknown>)['website'] = input.website;
    if (input.fax !== undefined) (mapped as Record<string, unknown>)['fax'] = input.fax;
    if (input.rib !== undefined) (mapped as Record<string, unknown>)['rib'] = input.rib;
    if (input.bankName !== undefined) (mapped as Record<string, unknown>)['bank_name'] = input.bankName;
    if (input.socialFacebook !== undefined) (mapped as Record<string, unknown>)['social_facebook'] = input.socialFacebook;
    if (input.socialInstagram !== undefined) (mapped as Record<string, unknown>)['social_instagram'] = input.socialInstagram;
    if (input.socialLinkedin !== undefined) (mapped as Record<string, unknown>)['social_linkedin'] = input.socialLinkedin;
    if (input.ticketWidthMm !== undefined) mapped['ticket_width_mm'] = input.ticketWidthMm;
    if (input.ticketHeightMm !== undefined) mapped['ticket_height_mm'] = input.ticketHeightMm;
    if (input.ticketShowName !== undefined) mapped['ticket_show_name'] = input.ticketShowName;
    if (input.ticketShowPrice !== undefined) mapped['ticket_show_price'] = input.ticketShowPrice;
    if (input.ticketPriceType !== undefined) mapped['ticket_price_type'] = input.ticketPriceType;
    if (input.ticketShowReference !== undefined) mapped['ticket_show_reference'] = input.ticketShowReference;
    if (input.ticketShowBarcode !== undefined) mapped['ticket_show_barcode'] = input.ticketShowBarcode;
    if (input.ticketShowCompanyName !== undefined) mapped['ticket_show_company_name'] = input.ticketShowCompanyName;
    if (input.ticketCurrencySymbol !== undefined) mapped['ticket_currency_symbol'] = input.ticketCurrencySymbol;
    if (input.ticketElementOrder !== undefined) mapped['ticket_element_order'] = input.ticketElementOrder.join(',');
    // Auto-increment codes — colonnes ajoutées dans la migration, pas encore dans database.types
    const m = mapped as Record<string, unknown>;
    if (input.clientCodePrefix !== undefined) m['client_code_prefix'] = input.clientCodePrefix;
    if (input.clientCodeNext !== undefined) m['client_code_next'] = input.clientCodeNext;
    if (input.clientCodePadding !== undefined) m['client_code_padding'] = input.clientCodePadding;
    if (input.supplierCodePrefix !== undefined) m['supplier_code_prefix'] = input.supplierCodePrefix;
    if (input.supplierCodeNext !== undefined) m['supplier_code_next'] = input.supplierCodeNext;
    if (input.supplierCodePadding !== undefined) m['supplier_code_padding'] = input.supplierCodePadding;
    if (input.articleCodePrefix !== undefined) m['article_code_prefix'] = input.articleCodePrefix;
    if (input.articleCodeNext !== undefined) m['article_code_next'] = input.articleCodeNext;
    if (input.articleCodePadding !== undefined) m['article_code_padding'] = input.articleCodePadding;
    if (input.barcodePrefix !== undefined) m['barcode_prefix'] = input.barcodePrefix;
    if (input.barcodeNext !== undefined) m['barcode_next'] = input.barcodeNext;
    if (input.barcodePadding !== undefined) m['barcode_padding'] = input.barcodePadding;

    const { data, error } = await this.supabase
      .from('company_settings')
      .update(mapped)
      .eq('id', current.id)
      .select()
      .single();
    if (error) throw error;
    return mapCompanySettings(data);
  }

  async getModuleSettings(): Promise<ModuleSetting[]> {
    const { data, error } = await this.supabase.from('module_settings').select('*');
    if (error) throw error;
    return (data ?? []).map((r) => ({
      moduleKey: r.module_key,
      isEnabled: r.is_enabled ?? false,
    }));
  }

  async updateModuleSetting(moduleKey: string, isEnabled: boolean): Promise<ModuleSetting> {
    // Resolve the caller's tenant_id so the upsert composite key
    // (tenant_id, module_key) matches the schema's PRIMARY KEY. We
    // upsert (not just update) because legacy tenants pre-dating
    // 20260426060000_backfill_module_settings might still be missing
    // a row for some keys; without an INSERT path the toggle would
    // silently no-op.
    //
    // The `tenants` table isn't yet in the auto-generated
    // database.types.ts (generated before the multi-tenant migration),
    // so we cast through `unknown` for the read; runtime shape is
    // verified by checking `error` and the `id` field. The cast goes
    // away when we re-run `supabase gen types typescript`.
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const tenantRes = (await (sb.from('tenants') as {
      select: (cols: string) => {
        limit: (n: number) => {
          maybeSingle: () => Promise<{
            data: { id: string } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).select('id').limit(1).maybeSingle());
    if (tenantRes.error) throw new Error(tenantRes.error.message);
    if (!tenantRes.data) throw new Error('not_in_tenant');
    const tenantId = tenantRes.data.id;

    const upsertRes = await (sb.from('module_settings') as {
      upsert: (
        row: { tenant_id: string; module_key: string; is_enabled: boolean },
        opts: { onConflict: string },
      ) => {
        select: () => {
          single: () => Promise<{
            data: { module_key: string; is_enabled: boolean | null } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).upsert(
      { tenant_id: tenantId, module_key: moduleKey, is_enabled: isEnabled },
      { onConflict: 'tenant_id,module_key' },
    ).select().single();

    if (upsertRes.error) throw new Error(upsertRes.error.message);
    if (!upsertRes.data) throw new Error('upsert_returned_no_row');
    return {
      moduleKey: upsertRes.data.module_key,
      isEnabled: upsertRes.data.is_enabled ?? false,
    };
  }

  // user_module_overrides isn't in the auto-generated database.types.ts
  // yet (table added after the last gen run), so we cast through unknown
  // for the typed-builder path. Same idiom as updateModuleSetting above.

  async getUserModuleOverrides(): Promise<UserModuleOverride[]> {
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const res = await (sb.from('user_module_overrides') as {
      select: (cols: string) => Promise<{
        data: { user_id: string; module_key: string; is_enabled: boolean | null }[] | null;
        error: { message: string } | null;
      }>;
    }).select('user_id, module_key, is_enabled');
    if (res.error) throw new Error(res.error.message);
    return (res.data ?? []).map((r) => ({
      userId: r.user_id,
      moduleKey: r.module_key,
      isEnabled: r.is_enabled ?? false,
    }));
  }

  async setUserModuleOverride(
    userId: string,
    moduleKey: string,
    isEnabled: boolean,
  ): Promise<UserModuleOverride> {
    // Resolve the caller's tenant_id so the upsert composite key
    // (tenant_id, user_id, module_key) matches the schema's PRIMARY KEY.
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const tenantRes = (await (sb.from('tenants') as {
      select: (cols: string) => {
        limit: (n: number) => {
          maybeSingle: () => Promise<{
            data: { id: string } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).select('id').limit(1).maybeSingle());
    if (tenantRes.error) throw new Error(tenantRes.error.message);
    if (!tenantRes.data) throw new Error('not_in_tenant');
    const tenantId = tenantRes.data.id;

    const upsertRes = await (sb.from('user_module_overrides') as {
      upsert: (
        row: { tenant_id: string; user_id: string; module_key: string; is_enabled: boolean },
        opts: { onConflict: string },
      ) => {
        select: () => {
          single: () => Promise<{
            data: { user_id: string; module_key: string; is_enabled: boolean | null } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).upsert(
      { tenant_id: tenantId, user_id: userId, module_key: moduleKey, is_enabled: isEnabled },
      { onConflict: 'tenant_id,user_id,module_key' },
    ).select().single();

    if (upsertRes.error) throw new Error(upsertRes.error.message);
    if (!upsertRes.data) throw new Error('upsert_returned_no_row');
    return {
      userId: upsertRes.data.user_id,
      moduleKey: upsertRes.data.module_key,
      isEnabled: upsertRes.data.is_enabled ?? false,
    };
  }

  async removeUserModuleOverride(userId: string, moduleKey: string): Promise<void> {
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const tenantRes = (await (sb.from('tenants') as {
      select: (cols: string) => {
        limit: (n: number) => {
          maybeSingle: () => Promise<{
            data: { id: string } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).select('id').limit(1).maybeSingle());
    if (tenantRes.error) throw new Error(tenantRes.error.message);
    if (!tenantRes.data) throw new Error('not_in_tenant');
    const tenantId = tenantRes.data.id;

    const delRes = await (sb.from('user_module_overrides') as {
      delete: () => {
        eq: (col: string, val: string) => {
          eq: (col: string, val: string) => {
            eq: (col: string, val: string) => Promise<{ error: { message: string } | null }>;
          };
        };
      };
    })
      .delete()
      .eq('tenant_id', tenantId)
      .eq('user_id', userId)
      .eq('module_key', moduleKey);
    if (delRes.error) throw new Error(delRes.error.message);
  }
}

function mapCompanySettings(row: Record<string, unknown>): CompanySettings {
  return {
    id: row['id'] as string,
    companyName: row['company_name'] as string,
    softwareName: (row['software_name'] as string) ?? null,
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    logoUrl: (row['logo_url'] as string) ?? null,
    logoDarkUrl: (row['logo_dark_url'] as string) ?? null,
    faviconUrl: (row['favicon_url'] as string) ?? null,
    logoSmallUrl: (row['logo_small_url'] as string) ?? null,
    sidebarBrandDisplay: parseSidebarBrandDisplay(row['sidebar_brand_display']),
    primaryColor: (row['primary_color'] as string) ?? null,
    borderRadius: (row['border_radius'] as string) ?? null,
    themeMode: (row['theme_mode'] as string) ?? null,
    themePreset: (row['theme_preset'] as string) ?? null,
    defaultVatRate: (row['default_vat_rate'] as number) ?? 19,
    withholdingThreshold: (row['withholding_threshold'] as number) ?? 1000,
    withholdingRate: (row['withholding_rate'] as number) ?? 1,
    fiscalStampAmount: Number(row['fiscal_stamp_amount'] ?? 1),
    defaultCurrency: (row['default_currency'] as string) ?? 'TND',
    reuseDeletedNumbers: (row['reuse_deleted_numbers'] as boolean) ?? false,
    showClientCarNumber: (row['show_client_car_number'] as boolean) ?? true,
    tagline: (row['tagline'] as string) ?? null,
    subtitle: (row['subtitle'] as string) ?? null,
    website: (row['website'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    rib: (row['rib'] as string) ?? null,
    bankName: (row['bank_name'] as string) ?? null,
    socialFacebook: (row['social_facebook'] as string) ?? null,
    socialInstagram: (row['social_instagram'] as string) ?? null,
    socialLinkedin: (row['social_linkedin'] as string) ?? null,
    ticketWidthMm: Number(row['ticket_width_mm'] ?? 40),
    ticketHeightMm: Number(row['ticket_height_mm'] ?? 20),
    ticketShowName: (row['ticket_show_name'] as boolean) ?? true,
    ticketShowPrice: (row['ticket_show_price'] as boolean) ?? true,
    ticketPriceType: ((row['ticket_price_type'] as TicketPriceType) ?? 'ttc'),
    ticketShowReference: (row['ticket_show_reference'] as boolean) ?? true,
    ticketShowBarcode: (row['ticket_show_barcode'] as boolean) ?? true,
    ticketShowCompanyName: (row['ticket_show_company_name'] as boolean) ?? false,
    ticketCurrencySymbol: (row['ticket_currency_symbol'] as string) ?? 'DT',
    ticketElementOrder: parseTicketOrder(row['ticket_element_order']),
    clientCodePrefix: (row['client_code_prefix'] as string) ?? 'CLI',
    clientCodeNext: Number(row['client_code_next'] ?? 1),
    clientCodePadding: Number(row['client_code_padding'] ?? 4),
    supplierCodePrefix: (row['supplier_code_prefix'] as string) ?? 'FRN',
    supplierCodeNext: Number(row['supplier_code_next'] ?? 1),
    supplierCodePadding: Number(row['supplier_code_padding'] ?? 4),
    articleCodePrefix: (row['article_code_prefix'] as string) ?? 'ART',
    articleCodeNext: Number(row['article_code_next'] ?? 1),
    articleCodePadding: Number(row['article_code_padding'] ?? 4),
    barcodePrefix: (row['barcode_prefix'] as string) ?? '',
    barcodeNext: Number(row['barcode_next'] ?? 1),
    barcodePadding: Number(row['barcode_padding'] ?? 8),
  };
}
